import React, { useRef } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Printer, Download, ArrowLeft, Building2, Mail, Phone, MapPin } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import BackButton from "@/components/navigation/BackButton";

interface ShippingAddress {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

const InvoicePage = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const printRef = useRef<HTMLDivElement>(null);

  const { data: order, isLoading, isError } = useQuery({
    queryKey: ['invoice-order', orderId],
    queryFn: async () => {
      if (!orderId) return null;
      
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (
            id,
            quantity,
            price,
            products (
              name,
              image_url,
              sku
            )
          )
        `)
        .eq('id', orderId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!orderId,
  });

  const { data: companySettings } = useQuery({
    queryKey: ['company-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('settings')
        .eq('id', 'main')
        .single();
      
      if (error) throw error;
      const settings = data?.settings as any || {};
      return {
        logoUrl: settings.logoUrl as string | undefined,
        logoText: settings.logoText as string | undefined,
      };
    },
  });

  const handlePrint = () => {
    window.print();
    toast({
      title: "Print dialog opened",
      description: "Your invoice is ready to print.",
    });
  };

  const renderAddress = (address: any) => {
    if (!address) return null;
    
    let addr: ShippingAddress;
    if (typeof address === 'string') {
      try {
        addr = JSON.parse(address);
      } catch {
        return <p className="text-sm text-foreground">{address}</p>;
      }
    } else {
      addr = address;
    }

    return (
      <div className="space-y-1">
        {addr.street && <p className="text-sm text-foreground">{addr.street}</p>}
        {(addr.city || addr.state || addr.zip) && (
          <p className="text-sm text-foreground">
            {addr.city && addr.city}
            {addr.city && addr.state && ', '}
            {addr.state && addr.state}
            {addr.zip && ` ${addr.zip}`}
          </p>
        )}
        {addr.country && <p className="text-sm text-foreground">{addr.country}</p>}
      </div>
    );
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 print:bg-gray-100 print:text-gray-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800 print:bg-gray-100 print:text-gray-800';
      case 'shipped':
        return 'bg-purple-100 text-purple-800 print:bg-gray-100 print:text-gray-800';
      case 'delivered':
        return 'bg-green-100 text-green-800 print:bg-gray-100 print:text-gray-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800 print:bg-gray-100 print:text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-4xl py-8 px-4 sm:px-6 lg:px-8">
        <Skeleton className="h-8 w-48 mb-6" />
        <Card variant="glass">
          <CardContent className="p-8">
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isError || !order) {
    return (
      <div className="container mx-auto max-w-4xl py-8 px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-2xl font-bold mb-4">Invoice Not Found</h2>
        <p className="text-muted-foreground mb-6">We couldn't find the invoice you're looking for.</p>
        <Button onClick={() => navigate('/orders')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Orders
        </Button>
      </div>
    );
  }

  const subtotal = order.order_items?.reduce((sum: number, item: any) => sum + (item.price * item.quantity), 0) || 0;
  const taxRate = 0.08; // 8% tax rate - could be configurable
  const taxAmount = subtotal * taxRate;
  const total = order.total_amount;

  return (
    <>
      <SEO title={`Invoice #${order.order_number || orderId?.slice(0, 8)}`} />
      
      {/* Print Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @media print {
            body { -webkit-print-color-adjust: exact; }
            .no-print { display: none !important; }
            .print-full-width { width: 100% !important; max-width: none !important; }
            .print-page { page-break-inside: avoid; }
            .print-header { margin-bottom: 2rem; }
            .print-table { border-collapse: collapse; width: 100%; }
            .print-table th, .print-table td { 
              border: 1px solid #ddd; 
              padding: 8px; 
              text-align: left; 
            }
            .print-table th { background-color: #f5f5f5; }
            .glass-effect { background: white !important; border: 1px solid #e5e7eb !important; }
          }
        `
      }} />

      <div className="min-h-screen bg-gradient-muted">
        {/* Header Actions - Hidden in print */}
        <div className="no-print bg-background/80 backdrop-blur-sm border-b sticky top-0 z-10">
          <div className="container mx-auto max-w-4xl py-4 px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center">
              <Button variant="outline" onClick={() => navigate('/orders')} className="bg-transparent hover:bg-white/10">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Orders
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handlePrint} className="bg-transparent hover:bg-white/10">
                  <Printer className="mr-2 h-4 w-4" />
                  Print Invoice
                </Button>
                <Button variant="outline" className="bg-transparent hover:bg-white/10">
                  <Download className="mr-2 h-4 w-4" />
                  Download PDF
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Invoice Content */}
        <div className="container mx-auto max-w-4xl py-8 px-4 sm:px-6 lg:px-8 print-full-width">
          <div className="no-print">
            <BackButton to={`/order/${orderId}`} label="Back to Order Details" useSmartNavigation />
          </div>
          <Card variant="glass" className="glass-effect print-page" ref={printRef}>
            <CardContent className="p-8 sm:p-12">
              
              {/* Header with Company Logo/Info */}
              <div className="print-header flex justify-between items-start mb-12">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    {companySettings?.logoUrl ? (
                      <img 
                        src={companySettings.logoUrl} 
                        alt="Company Logo" 
                        className="h-12 w-auto max-h-12" 
                      />
                    ) : (
                      <div className="h-12 w-12 bg-gradient-luxury rounded-lg flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-white" />
                      </div>
                    )}
                    <div>
                      <h1 className="text-2xl font-bold text-foreground font-playfair">
                        {companySettings?.logoText || 'VRAM Store'}
                      </h1>
                      <p className="text-sm text-muted-foreground">Premium Electronics & Technology</p>
                    </div>
                  </div>
                  
                  <div className="space-y-1 text-sm text-muted-foreground mt-4">
                    <div className="flex items-center gap-2">
                      <Mail className="h-3 w-3" />
                      <span>contact@vramstore.com</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="h-3 w-3" />
                      <span>+1 (555) 123-4567</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-3 w-3" />
                      <span>123 Technology Boulevard, Silicon Valley, CA 94000</span>
                    </div>
                  </div>
                </div>
                
                <div className="text-right">
                  <h2 className="text-3xl font-bold text-foreground font-playfair mb-2">INVOICE</h2>
                  <div className="space-y-1 text-sm">
                    <p><span className="font-medium">Invoice #:</span> {order.order_number}</p>
                    <p><span className="font-medium">Date:</span> {format(new Date(order.created_at), 'MMMM dd, yyyy')}</p>
                    <p><span className="font-medium">Due Date:</span> {format(new Date(order.created_at), 'MMMM dd, yyyy')}</p>
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">Bill To:</h3>
                  <div className="space-y-1">
                    <p className="font-medium text-foreground">{order.customer_name || 'Customer'}</p>
                    <p className="text-sm text-muted-foreground">{order.customer_email}</p>
                    {order.billing_address && renderAddress(order.billing_address)}
                  </div>
                </div>
                
                {order.shipping_address && (
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">Ship To:</h3>
                    {renderAddress(order.shipping_address)}
                  </div>
                )}
              </div>

              {/* Order Status */}
              <div className="flex justify-between items-center mb-8 p-4 bg-muted/20 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Order Status</p>
                  <Badge className={getStatusColor(order.status)}>{order.status}</Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Payment Status</p>
                  <Badge className={getStatusColor(order.payment_status)}>{order.payment_status}</Badge>
                </div>
                {order.tracking_number && (
                  <div>
                    <p className="text-sm text-muted-foreground">Tracking Number</p>
                    <p className="font-mono text-sm font-medium">{order.tracking_number}</p>
                  </div>
                )}
              </div>

              {/* Items Table */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-foreground mb-4">Order Items</h3>
                <div className="overflow-x-auto">
                  <table className="print-table w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2 font-medium text-foreground">Item</th>
                        <th className="text-center py-3 px-2 font-medium text-foreground">SKU</th>
                        <th className="text-center py-3 px-2 font-medium text-foreground">Qty</th>
                        <th className="text-right py-3 px-2 font-medium text-foreground">Unit Price</th>
                        <th className="text-right py-3 px-2 font-medium text-foreground">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.order_items?.map((item: any, index: number) => (
                        <tr key={item.id} className={index % 2 === 0 ? 'bg-muted/10' : ''}>
                          <td className="py-4 px-2">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 bg-muted/30 rounded-lg flex items-center justify-center shrink-0 no-print">
                                {item.products?.image_url ? (
                                  <img 
                                    src={item.products.image_url} 
                                    alt={item.products.name} 
                                    className="w-full h-full object-cover rounded-lg"
                                  />
                                ) : (
                                  <Building2 className="h-4 w-4 text-muted-foreground" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-foreground">{item.products?.name}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-2 text-center text-sm text-muted-foreground">
                            {item.products?.sku || 'N/A'}
                          </td>
                          <td className="py-4 px-2 text-center text-sm text-foreground">
                            {item.quantity}
                          </td>
                          <td className="py-4 px-2 text-right text-sm text-foreground">
                            ${Number(item.price).toFixed(2)}
                          </td>
                          <td className="py-4 px-2 text-right font-medium text-foreground">
                            ${(Number(item.price) * item.quantity).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-full max-w-sm space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal:</span>
                    <span className="text-foreground">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Tax (8%):</span>
                    <span className="text-foreground">${taxAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Shipping:</span>
                    <span className="text-foreground">$0.00</span>
                  </div>
                  <div className="border-t pt-2">
                    <div className="flex justify-between font-bold text-lg">
                      <span className="text-foreground">Total:</span>
                      <span className="text-foreground">${Number(total).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-12 pt-8 border-t">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-sm text-muted-foreground">
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Payment Information</h4>
                    <p>Payment Method: {order.payment_method || 'Credit Card'}</p>
                    <p>Payment Status: {order.payment_status}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Terms & Conditions</h4>
                    <p className="text-xs leading-relaxed">
                      Payment is due upon receipt. Returns accepted within 30 days with original receipt. 
                      All sales final on custom orders. For questions, contact customer service.
                    </p>
                  </div>
                </div>
                
                <div className="text-center mt-8 pt-4 border-t">
                  <p className="text-xs text-muted-foreground">
                    Thank you for your business! This invoice was generated on {format(new Date(), 'MMMM dd, yyyy')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default InvoicePage;